import React, { FC, useEffect, useRef, useState } from "react";

interface Poster {
  webp: string;
  jpg: string;
}

interface Props {
  /** Class carrying the absolute-fill background styles, e.g. "homeHero--video". */
  className: string;
  desktopSrc: string;
  mobileSrc: string;
  desktopPoster: Poster;
  mobilePoster: Poster;
  minWidth?: number;
}

/**
 * Autoplaying background video that paints instantly and downloads late.
 *
 * The <img> poster (a compressed still of the video's first frame) is the
 * LCP candidate: it sits in the initial HTML and is fetched with
 * fetchpriority=high. The multi-megabyte mp4 is not referenced in the
 * markup at all (preload=none, no <source> children) so it cannot compete
 * with the poster, CSS or hydration for bandwidth - Lighthouse's simulated
 * LCP was dominated by exactly that contention. Once the window `load`
 * event has fired, the viewport-appropriate file is attached and played,
 * and the video fades in over the poster. Browsers that refuse playback
 * (data saver, low-power mode) simply keep showing the poster. Picking the
 * src explicitly via matchMedia also sidesteps Safari's unreliable
 * media-attribute <source> selection that useResponsiveVideoSource used
 * to correct after the fact.
 */
export const HeroVideo: FC<Props> = ({
  className,
  desktopSrc,
  mobileSrc,
  desktopPoster,
  mobilePoster,
  minWidth = 1024,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;
    const start = () => {
      if (cancelled || video.src) return;
      video.src = window.matchMedia(`(min-width: ${minWidth}px)`).matches
        ? desktopSrc
        : mobileSrc;
      video.load();
      video.play().catch(() => undefined);
    };

    if (document.readyState === "complete") {
      start();
      return () => {
        cancelled = true;
      };
    }
    window.addEventListener("load", start, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener("load", start);
    };
  }, [desktopSrc, mobileSrc, minWidth]);

  const desktopMedia = `(min-width: ${minWidth}px)`;

  return (
    <>
      <picture>
        <source
          media={desktopMedia}
          srcSet={desktopPoster.webp}
          type="image/webp"
        />
        <source media={desktopMedia} srcSet={desktopPoster.jpg} />
        <source srcSet={mobilePoster.webp} type="image/webp" />
        <img
          src={mobilePoster.jpg}
          alt=""
          className={className}
          decoding="async"
          // React 18.2 does not know the camelCase fetchPriority prop yet,
          // but passes unknown lowercase attributes straight through.
          {...({ fetchpriority: "high" } as any)}
        />
      </picture>
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        preload="none"
        className={className}
        style={{
          opacity: isPlaying ? 1 : 0,
          transition: "opacity 0.5s ease",
          // Inset the video 1px inside the poster so its painted area is
          // strictly smaller. Chrome treats the late first frame of the
          // playing video as a new LCP candidate, re-anchoring LCP from
          // the poster paint (~1 s) to the post-load video swap. Since a
          // new candidate must be strictly larger to take over, the 1px
          // sliver of the identical poster showing around the video keeps
          // the metric on the poster; visually nothing changes.
          top: "1px",
          left: "1px",
          width: "calc(100% - 2px)",
          height: "calc(100% - 2px)",
        }}
        onPlaying={() => setIsPlaying(true)}
      >
        Your browser does not support the video tag.
      </video>
    </>
  );
};
